import type { Expense } from "@porchpass/ui";
import { type DomainExpenseType as DomainExpenseTypeValue } from "@porchpass/api-client";
import type { ExpenseFormData } from "src/types/expenses";
import { fieldErrorsAfterSubmitRecord } from "src/forms/fieldErrorsAfterSubmit";

const UI_TO_DLA_FIELD: Partial<Record<keyof Expense, keyof ExpenseFormData>> = {
  monthlyAmount: "amount",
  typeOther: "expense_type_other",
  startMonth: "from_month",
  startYear: "from_year",
  endMonth: "to_month",
  endYear: "to_year",
};

function parseChildrenAges(value?: string): number[] | undefined {
  if (!value?.trim()) return undefined;
  const ages = value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "" && !Number.isNaN(Number(part)))
    .map(Number);
  return ages.length > 0 ? ages : undefined;
}

function formatChildrenAges(ages?: number[]): string | undefined {
  if (!ages?.length) return undefined;
  return ages.join(",");
}

export function toUiExpense(dto: ExpenseFormData): Expense {
  const isOngoing = dto.is_ongoing ?? (!dto.to_month && !dto.to_year);

  return {
    id: dto.id,
    type: (dto.expense_type ?? "") as DomainExpenseTypeValue,
    monthlyAmount:
      dto.amount != null && dto.amount !== "" ? Number(dto.amount) : undefined,
    typeOther: dto.expense_type_other,
    startMonth: dto.from_month,
    startYear: dto.from_year,
    endMonth: dto.to_month,
    endYear: dto.to_year,
    isOngoing,
    childrenAges: parseChildrenAges(dto.children_ages),
  };
}

export function fromUiExpensePatch(
  patch: Partial<Expense>,
): Partial<ExpenseFormData> {
  const mapped: Partial<ExpenseFormData> = {};

  for (const [uiKey, value] of Object.entries(patch)) {
    if (value === undefined) continue;

    if (uiKey === "childrenAges") {
      mapped.children_ages = formatChildrenAges(value as number[]);
      continue;
    }

    if (uiKey === "isOngoing") {
      mapped.is_ongoing = value as boolean;
      if (value) {
        mapped.to_month = "";
        mapped.to_year = "";
      }
      continue;
    }

    const dlaKey = UI_TO_DLA_FIELD[uiKey as keyof Expense];
    if (!dlaKey) continue;

    if (uiKey === "monthlyAmount") {
      mapped.amount = String(value);
    } else if (dlaKey === "expense_type_other") {
      mapped.expense_type_other = value as string;
    } else if (
      dlaKey === "from_month" ||
      dlaKey === "from_year" ||
      dlaKey === "to_month" ||
      dlaKey === "to_year"
    ) {
      mapped[dlaKey] = value as string;
    }
  }

  return mapped;
}

export function expenseErrorsAfterSubmit<
  T extends {
    amount?: { message?: string };
    expense_type_other?: { message?: string };
    from_month?: { message?: string };
    from_year?: { message?: string };
    to_month?: { message?: string };
    to_year?: { message?: string };
    children_ages?: { message?: string };
  },
>(formErrors: T, hasAttemptedFormSubmission: boolean): Record<string, string> {
  return fieldErrorsAfterSubmitRecord(
    {
      monthlyAmount: formErrors.amount?.message,
      typeOther: formErrors.expense_type_other?.message,
      startMonth: formErrors.from_month?.message,
      startYear: formErrors.from_year?.message,
      endMonth: formErrors.to_month?.message,
      endYear: formErrors.to_year?.message,
      childrenAges: formErrors.children_ages?.message,
    },
    hasAttemptedFormSubmission,
  );
}
